/**
 * Decides whether the full-screen transition overlay should be suppressed when
 * the session re-enters Solo Time for the next question.
 *
 * The backend maps `challenge` to `color_screen` unconditionally
 * (turn_taking.py `state_to_screen_map`), so without this rule the overlay stays
 * up — painted blue, because `challenge` is a Solo phase — for the whole
 * `challenge` state before `gather` clears it. Students should see the next
 * question instead.
 *
 * Only the *mid-session* re-entry is suppressed. The Solo Time screen that opens
 * the session must still play, and it is distinguished by its previous state:
 * at a `challenge` transition the backend's `previous_state` comes from the
 * previous task's last completed subtask, so it can only be `introduce` (first
 * challenge), `solution` / `feedback_analysis` (a completed team round),
 * `evaluate` (non-consensus strategies), or null. `discuss` and `explanation`
 * never precede `challenge` — hence the narrow set below.
 *
 * Pure and dependency-free so the rule itself is unit-tested, matching the
 * phaseDwellTracker pattern.
 */

// Team-time states the backend can report as the state completed immediately
// before re-entering `challenge`. `introduce` is deliberately excluded so the
// session-entry Solo Time screen is preserved.
const TEAM_EXIT_STATES = new Set(["solution", "feedback_analysis"]);

/**
 * True when this state change is a mid-session Team Time -> Solo Time move whose
 * transition overlay should not be shown.
 *
 * `hasSurvey` guards the challenge/session survey flows, which reuse the same
 * overlay component to render survey UI — suppressing it there would hide the
 * survey itself, not just a background.
 */
export const shouldSuppressSoloTransition = ({ state, previousState, hasSurvey } = {}) =>
  state === "challenge" && TEAM_EXIT_STATES.has(previousState) && !hasSurvey;
